import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LayoutDashboard,
  Search,
  Megaphone,
  Users,
  MessageSquare,
  Calendar,
  Mail,
  FileText,
  Image as ImageIcon,
  Map,
  LogOut,
  Compass,
  Settings as SettingsIcon,
} from "lucide-react";

const NAV = [
  { to: "/app", label: "Inicio", icon: LayoutDashboard, end: true, testid: "nav-home" },
  { to: "/app/seo", label: "SEO", icon: Search, testid: "nav-seo" },
  { to: "/app/ads", label: "Anuncios", icon: Megaphone, testid: "nav-ads" },
  { to: "/app/leads", label: "Leads CRM", icon: Users, testid: "nav-leads" },
  { to: "/app/calendar", label: "Calendario", icon: Calendar, testid: "nav-calendar" },
  { to: "/app/mail", label: "Email Marketing", icon: Mail, testid: "nav-mail" },
  { to: "/app/landings", label: "Landings", icon: FileText, testid: "nav-landings" },
  { to: "/app/itineraries", label: "Itinerarios", icon: Map, testid: "nav-itineraries" },
  { to: "/app/library", label: "Biblioteca", icon: ImageIcon, testid: "nav-library" },
  { to: "/app/settings", label: "Configuración", icon: SettingsIcon, testid: "nav-settings" },
];

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex bg-[#F9F8F6] topo-bg">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-[#E2DCD0] bg-[#F9F8F6]/90 backdrop-blur">
        <div className="px-6 py-7 border-b border-[#E2DCD0]">
          <div className="flex items-center gap-2">
            <Compass className="w-5 h-5 text-[#D95D39]" strokeWidth={1.5} />
            <span className="font-display text-xl tracking-tight text-[#0A1128]">VoyageMarkt</span>
          </div>
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98] mt-1">
            Travel · Marketing · Studio
          </p>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-0.5">
          {NAV.map(({ to, label, icon: Icon, end, testid }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-colors",
                  isActive
                    ? "bg-[#0A1128] text-white"
                    : "text-[#1A1F2B] hover:bg-[#E2DCD0]/40",
                ].join(" ")
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-[#E2DCD0] p-4">
          <div className="text-xs text-[#5C616A] mb-2">
            <div className="font-medium text-[#1A1F2B] truncate">{user?.name}</div>
            <div className="truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-button"
            onClick={() => { logout(); nav("/login"); }}
            className="w-full flex items-center justify-center gap-2 text-sm border border-[#E2DCD0] hover:border-[#D95D39] hover:text-[#D95D39] rounded-sm py-2 transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* Mobile glass header */}
        <header className="lg:hidden sticky top-0 z-30 glass-header px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Compass className="w-4 h-4 text-[#D95D39]" strokeWidth={1.5} />
            <span className="font-display text-lg text-[#0A1128]">VoyageMarkt</span>
          </div>
          <button
            onClick={() => { logout(); nav("/login"); }}
            className="text-xs text-[#5C616A]"
            data-testid="logout-button-mobile"
          >
            Salir
          </button>
        </header>

        {/* Mobile bottom nav */}
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 glass-header border-t border-[#E2DCD0] flex overflow-x-auto">
          {NAV.map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                [
                  "flex flex-col items-center justify-center min-w-[68px] py-2 text-[10px]",
                  isActive ? "text-[#D95D39]" : "text-[#5C616A]",
                ].join(" ")
              }
            >
              <Icon className="w-4 h-4 mb-1" strokeWidth={1.5} />
              <span className="truncate">{label.split(" ")[0]}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-5 sm:px-8 lg:px-12 py-8 lg:py-10 pb-24 lg:pb-10 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
